import React, { useState } from "react";
import { useHistory, Link } from "react-router-dom";
import { Button, Card, Form, Alert, Row, Col } from "react-bootstrap";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import AsyncSelect from "react-select/async";
import { colors, constants, generateRoutePath, CLIENT_TYPES, US_STATES } from "utils";
import { request } from "hooks/useOkoApi/useOkoApi";

const PageContainer = styled.div`
  max-width: 800px;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${colors.nightlyWoods700};
  text-decoration: none;
  margin-bottom: 16px;
  &:hover {
    color: ${colors.nightlyWoods900};
  }
`;

const PageTitle = styled.h2`
  margin: 0 0 24px;
  color: ${colors.nightlyWoods900};
`;

const FormCard = styled(Card)`
  margin-bottom: 24px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 24px;
`;

export const ClientCreate = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    client_type: "district",
    state: "",
    nces_lea_id: "",
    state_organization_id: "",
    hubspot_company_id: "",
    hubspot_deal_id: "",
    billing_email: "",
    billing_contact_name: "",
    notes: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const searchDistricts = async (inputValue) => {
    if (!inputValue || inputValue.length < 2) return [];
    try {
      const response = await request(`admin/districts/search?q=${encodeURIComponent(inputValue)}&limit=20`, { method: "GET" });
      return response.results.map(district => ({
        value: district.lea_id,
        label: `${district.lea_id} - ${district.name} (${district.state || 'Unknown'})`,
        data: district,
      }));
    } catch (err) {
      console.error("Failed to search districts:", err);
      return [];
    }
  };

  const handleDistrictSelect = (option) => {
    setSelectedDistrict(option);
    if (option) {
      setFormData(prev => ({
        ...prev,
        name: option.data.name,
        nces_lea_id: option.data.lea_id || "",
        state_organization_id: option.data.state_lea_id || "",
        state: option.data.state || prev.state,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        nces_lea_id: "",
        state_organization_id: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError("Name is required.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const payload = { ...formData };
      Object.keys(payload).forEach(key => {
        if (payload[key] === "") payload[key] = null;
      });
      payload.name = formData.name.trim();

      await request("admin/clients", {
        method: "POST",
        payload,
      });
      history.push(generateRoutePath(constants.CLIENT_ADMIN_ROUTE_NAME));
    } catch (err) {
      console.error("Failed to create client:", err);
      setError("Failed to create client. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    history.push(generateRoutePath(constants.CLIENT_ADMIN_ROUTE_NAME));
  };

  return (
    <PageContainer>
      <BackLink to={generateRoutePath(constants.CLIENT_ADMIN_ROUTE_NAME)}>
        <FontAwesomeIcon icon={faArrowLeft} /> Back to Clients
      </BackLink>

      <PageTitle>Create Client</PageTitle>

      {error && <Alert variant="danger">{error}</Alert>}

      <FormCard>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter client name"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Type *</Form.Label>
                  <Form.Select name="client_type" value={formData.client_type} onChange={handleChange}>
                    {CLIENT_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>State</Form.Label>
                  <Form.Select
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                  >
                    <option value="">Select state...</option>
                    {US_STATES.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>NCES LEA ID</Form.Label>
                  <AsyncSelect
                    cacheOptions
                    loadOptions={searchDistricts}
                    onChange={handleDistrictSelect}
                    value={selectedDistrict}
                    placeholder="Search or enter ID..."
                    isClearable
                    noOptionsMessage={({ inputValue }) =>
                      inputValue.length < 2
                        ? "Type 2+ chars to search"
                        : "No districts found"
                    }
                    formatOptionLabel={(option, { context }) =>
                      context === "value" ? option.data?.lea_id || option.value : option.label
                    }
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderColor: colors.nightlyWoods300,
                        minHeight: '38px',
                      }),
                    }}
                  />
                  <Form.Text className="text-muted">
                    Search 19,630 districts. Auto-fills name & state.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>State Organization ID</Form.Label>
                  <Form.Control
                    type="text"
                    name="state_organization_id"
                    value={formData.state_organization_id}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>HubSpot Company ID</Form.Label>
                  <Form.Control
                    type="text"
                    name="hubspot_company_id"
                    value={formData.hubspot_company_id}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>HubSpot Deal ID</Form.Label>
                  <Form.Control
                    type="text"
                    name="hubspot_deal_id"
                    value={formData.hubspot_deal_id}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Billing Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="billing_email"
                    value={formData.billing_email}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Billing Contact Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="billing_contact_name"
                    value={formData.billing_contact_name}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Internal notes about this client"
              />
            </Form.Group>

            <ButtonGroup>
              <Button variant="outline-secondary" onClick={handleCancel} disabled={loading}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                style={{ backgroundColor: colors.nightlyWoods500, borderColor: colors.nightlyWoods500 }}
              >
                {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : "Create Client"}
              </Button>
            </ButtonGroup>
          </Form>
        </Card.Body>
      </FormCard>
    </PageContainer>
  );
};
